import React, { useMemo, useState } from "react";
import GridLayout from "react-grid-layout";

// TILE COMPONENTS
import InformationTile from '../tiles/informationTileComponennt';
import LinkTile from '../tiles/linkTileComponent';
import NotificationTile from '../tiles/notificationTileComponent';
import ButtonTile from '../tiles/buttonTileComponent';
import CountdownTile from '../tiles/countdownTileComponent';

// COMPONENTS
import SearchBar from '../administration/searchBarComponent';

// UTILS
import { formatTileData } from 'utils/formatTileData';
import iconMap from 'utils/iconMap';

const UnusedTilesGrid = ({ unusedTiles, onRestore, isDesktopMode }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const HeadbandNewIcon = iconMap['headbandNew'];

    const renderTileContent = (tile) => {
        switch (tile.type) {
            case 'information':
                return <InformationTile icon={tile.data.icon} value={tile.data.value} unit={tile.data.unit} isEditing={true} />;
            case 'link':
                return <LinkTile tile_id={tile.id} title={tile.data.title} desc={tile.data.description} url={tile.data.url} isEditing={true} userEmail={null} />;
            case 'notification':
                return <NotificationTile title={tile.data.title} url={tile.data.url} notif={tile.notification} isEditing={true} />;
            case 'button':
                return <ButtonTile title={tile.data.title} data={tile.data} color={tile.color} isEditing={true} />;
            case 'countdown':
                return <CountdownTile title={tile.data.title} event={tile.evenement} isEditing={true} />;
            default:
                return <div>Erreur: Type de tuile inconnu</div>;
        }
    };

    const formattedTiles = useMemo(() => {
        return unusedTiles.map(tile => formatTileData(tile));
    }, [unusedTiles]);

    const filteredTiles = useMemo(() => {
        if (!searchQuery.trim()) return formattedTiles;

        const query = searchQuery.toLowerCase();
        return formattedTiles.filter(tile => {
            const searchableContent = [
                tile.data?.title,
                tile.data?.description,
                tile.data?.url,
                tile.data?.value,
                tile.data?.unit,
                tile.type
            ].filter(Boolean).join(" ").toLowerCase();

            return searchableContent.includes(query);
        });
    }, [formattedTiles, searchQuery]);

    const layout = filteredTiles.map((tile, index) => ({
        i: tile.id.toString(),
        x: index % 2,
        y: Math.floor(index / 2),
        w: tile.min_width || 1,
        h: tile.min_height || 1,
        minW: tile.min_width || 1,
        minH: tile.min_height || 1,
        static: false
    }));

    return (
        <div className="pt-2 flex-1 min-h-0 flex flex-col">
            {/* Header fixe */}
            <div className="flex-shrink-0">
                <h3 className="text-lg font-semibold mb-2 text-noir dark:text-blanc text-center">
                    Bibliothèque de tuiles
                </h3>
                <div className="px-2 mb-4">
                    <SearchBar
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Rechercher une tuile..."
                    />
                </div>
                <p className="text-sm text-gris text-center mb-2">
                    Cliquez sur une tuile pour l'ajouter à la page.
                </p>
            </div>

            {/* Zone scrollable pour les tuiles */}
            <div className="flex-1 min-h-0 overflow-y-auto">
                {filteredTiles.length === 0 ? (
                    <div className="h-full flex flex-col justify-center items-center text-center text-gris">
                        {searchQuery ? "Aucune tuile ne correspond à votre recherche." : "Vous avez utilisé toutes les tuiles disponibles."}
                    </div>
                ) : (
                    <div className="px-2">
                        <GridLayout
                            className={"layout not-resizable"}
                            layout={layout}
                            cols={2}
                            rowHeight={120}
                            width={290}
                            margin={[10, 10]}
                            isDraggable={false}
                            isResizable={false}
                            style={{ position: 'relative', height: 'auto' }}
                        >
                            {filteredTiles.map((tile) => (
                                <div
                                    key={tile.id}
                                    className="relative rounded-3xl cursor-pointer overflow-hidden"
                                    onClick={() => onRestore?.(tile.id)}
                                >
                                    <div
                                        className="w-full h-full p-2 rounded-3xl flex items-center justify-center"
                                        style={{ backgroundColor: tile.color }}
                                    >
                                        {tile.createdAt && new Date() - new Date(tile.createdAt) < 864000000 && (
                                            <div className="absolute top-1 left-1">
                                                <HeadbandNewIcon className="size-8 fill-blanc" />
                                            </div>
                                        )}
                                        {renderTileContent(tile)}
                                    </div>
                                </div>
                            ))}
                        </GridLayout>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UnusedTilesGrid;